/**
 * CR-5312: confirm before delete/deactivate. The backend decides which one
 * based on the user's login + audit footprint, but surface the *intent* to the
 * admin so they understand why inactive users can't be hard-deleted.
 * Extracted from UsersManagement to keep files under 500 lines.
 */

import type { UserProfile } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Trash2 } from 'lucide-react';

interface DeleteUserDialogProps {
  /** The user awaiting confirmation; null keeps the dialog closed. */
  candidate: UserProfile | null;
  /** Clear the candidate (Cancel, Escape, outside click). */
  onCancel: () => void;
  /** Perform the delete for the confirmed user. */
  onConfirm: (user: UserProfile) => Promise<void>;
  actionLoading: boolean;
}

export default function DeleteUserDialog({
  candidate, onCancel, onConfirm, actionLoading,
}: DeleteUserDialogProps) {
  return (
    <Dialog open={!!candidate} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <Trash2 className="h-5 w-5" />
            Delete user
          </DialogTitle>
          <DialogDescription>
            {candidate ? (
              <>
                <span className="font-medium">{candidate.user.full_name || candidate.user.email}</span>
                {' '}will be{' '}
                <span className="font-medium">permanently deleted</span>.
                If they have login or audit history, their records are kept
                under an anonymised identity — either way the account is
                removed and the email address can be invited again.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={actionLoading}
            onClick={async () => {
              if (!candidate) return;
              const target = candidate;
              onCancel();
              await onConfirm(target);
            }}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
